import strings from '../../../strings/strings.json';
import { breakpoints, sizes } from '../../../theme';
import Table, { TableCell, TableRow } from '../../../components/table/Table';
import { UserModel } from '../../../models/User';
import HeaderWithCount from '../components/HeaderWithCount';
import { IconSize } from 'assets/icons/icon-sizes';
import MainSection from 'components/sections/MainSection';
import styled from 'styled-components';
import PlusIcon from 'assets/icons/controls/PlusIcon';
import { BodyRegular, FlexAlignCenter, FlexEnd, Headline3 } from 'components/Elements';
import React, { useContext } from 'react';
import Modal from 'components/modals/Modal';
import RolesTags from 'pages/admin/components/RolesTags';
import { InviteUserForm } from './InviteUserForm';
import { OADashboardContext } from 'contextProviders/OADashboardContext';
import NextIcon from 'assets/icons/controls/NextIcon';
import { useNavigate } from 'react-router-dom';
import Button from 'components/buttons/Button';

export function OAUsersTable(): JSX.Element {
    const navigate = useNavigate();
    const users = useContext(OADashboardContext).users!;
    const { refetchUsers } = useContext(OADashboardContext);
    const [showInviteUserModal, setShowInviteUserModal] = React.useState<boolean>(false);
    const [activeUserId, setActiveUserId] = React.useState<string>('');

    const scrollRef = React.createRef<any>();
    React.useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    });

    const userRows = users.map((user: UserModel) => {
        const userName = `${user.firstName} ${user.lastName}`;

        const cells: TableCell[] = [
            {
                name: strings.OAUsersTable.tableHeaders.userName,
                value: (
                    <NameAndEmailContainer ref={activeUserId === user.id ? scrollRef : null}>
                        <BodyRegular $bold>{userName}</BodyRegular>
                        <BodyRegular>{user.email}</BodyRegular>
                    </NameAndEmailContainer>
                ),
            },
            {
                name: strings.OAUsersTable.tableHeaders.roles,
                value: <RolesTags roles={user.roles.map((r) => r.name)} />,
            },
            {
                name: '',
                value: (
                    <FlexEnd>
                        <Button
                            title={strings.OAUsersTable.viewUserButton.title}
                            aria={strings.OAUsersTable.viewUserButton.aria}
                            icon={<NextIcon />}
                            onClick={() => {
                                navigate(`/organisation/dashboard/users/${user.id}`);
                            }}
                            iconOnRight
                        />
                    </FlexEnd>
                ),
            },
        ];

        const tableRow: TableRow = { id: user.id, cells: cells };
        return tableRow;
    });

    return (
        <ManageUsersSection>
            {showInviteUserModal && (
                <Modal
                    onClickCloseButton={() => setShowInviteUserModal(false)}
                    headlineText={strings.inviteTrainerForm.heading}
                    modalBody={
                        <InviteUserForm
                            closeModal={async (trainerId: string) => {
                                setShowInviteUserModal(false);
                                await refetchUsers();
                                setActiveUserId(trainerId);
                            }}
                        />
                    }
                />
            )}
            <TitleAndButtonContainer>
                <Headline3>{strings.OAUsersTable.usersPageHeader}</Headline3>
                {userRows.length > 0 && (
                    <HeaderWithCount
                        countText={`${userRows.length} ${strings.OAUsersTable.numberOfUsersText}`}
                        actionButton={
                            <Button
                                title={strings.OAUsersTable.inviteUserButton.title}
                                aria={strings.OAUsersTable.inviteUserButton.aria}
                                onClick={() => setShowInviteUserModal(true)}
                                icon={<PlusIcon size={IconSize.LARGE} />}
                                colour='PRIMARY'
                            />
                        }
                    />
                )}
            </TitleAndButtonContainer>
            <div>
                <Table
                    headers={[
                        { name: strings.OAUsersTable.tableHeaders.userName, rowPercentage: 35 },
                        { name: strings.OAUsersTable.tableHeaders.roles, rowPercentage: 50 },
                        { name: '', rowPercentage: 15, mobileRightColumn: true },
                    ]}
                    rows={userRows}
                    activeRow={activeUserId}
                />
            </div>
        </ManageUsersSection>
    );
}

const ManageUsersSection = styled(MainSection)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
    padding: ${sizes.spacingSm} 0;
    min-height: 120px;

    @media (max-width: ${breakpoints.sm}) {
        padding: unset;
    }
`;

const TitleAndButtonContainer = styled(FlexAlignCenter)`
    justify-content: space-between;

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
        align-items: flex-start;
    }
`;

const NameAndEmailContainer = styled.div`
    display: flex;
    flex-direction: column;
`;
